import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';
import { ShareLink, SharePermission } from '../../types/shares';

import { getDriveApiToken } from '../../lib/auth-token';
// Create API service with base URL
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api',
});

// Add auth token to requests
api.interceptors.request.use(config => {
  const token = getDriveApiToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Types - Use union type for shares that can be either ShareLink or SharePermission
type Share = ShareLink | SharePermission;

interface ShareState {
  myShared: Share[];
  sharedWithMe: Share[];
  loading: boolean;
  error: string | null;
  currentShareLink: string | null;
}

// Initial state
const initialState: ShareState = {
  myShared: [],
  sharedWithMe: [],
  loading: false,
  error: null,
  currentShareLink: null,
};

// Async Thunks
export const fetchMyShared = createAsyncThunk(
  'share/fetchMyShared',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/shares/my-shared');
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch shares');
    }
  }
);

export const fetchSharedWithMe = createAsyncThunk(
  'share/fetchSharedWithMe',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/shares/shared-with-me');
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch shared items');
    }
  }
);

export const createFileShare = createAsyncThunk(
  'share/createFileShare',
  async (
    { fileId, isPublic, expiresAt, password }: 
    { fileId: string; isPublic: boolean; expiresAt?: string; password?: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.post(
        `/shares/file/${fileId}`,
        { isPublic, expiresAt, password }
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to share file');
    }
  }
);

export const createFolderShare = createAsyncThunk(
  'share/createFolderShare',
  async (
    { folderId, isPublic, expiresAt, password }: 
    { folderId: string; isPublic: boolean; expiresAt?: string; password?: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.post(
        `/shares/folder/${folderId}`,
        { isPublic, expiresAt, password }
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to share folder');
    }
  }
);

export const shareWithUsers = createAsyncThunk(
  'share/shareWithUsers',
  async (
    { resourceType, resourceId, userIds, shareType }: {
      resourceType: 'file' | 'folder';
      resourceId: string;
      userIds: string[];
      shareType: 'view' | 'edit' | 'admin';
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.post(
        `/shares/with-users/${resourceId}`,
        {
          resourceType,
          userIds,
          shareType
        }
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to share with users');
    }
  }
);

export const removeShare = createAsyncThunk(
  'share/removeShare',
  async (shareId: string, { rejectWithValue }) => {
    try {
      await api.delete(`/shares/${shareId}`);
      return { shareId };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to remove share');
    }
  }
);

export const updateShareExpiration = createAsyncThunk(
  'share/updateShareExpiration',
  async (
    { shareId, expiresAt }: { shareId: string; expiresAt: string | null },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.patch(`/shares/${shareId}`, { expiresAt });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update share expiration');
    }
  }
);

export const updateSharePassword = createAsyncThunk(
  'share/updateSharePassword',
  async (
    { shareId, password }: { shareId: string; password: string | null },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.patch(`/shares/${shareId}`, { password });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update share password');
    }
  }
);

// Slice
const shareSlice = createSlice({
  name: 'share',
  initialState,
  reducers: {
    clearShareError: (state) => {
      state.error = null;
    },
    setCurrentShareLink: (state, action: PayloadAction<string | null>) => {
      state.currentShareLink = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchMyShared
      .addCase(fetchMyShared.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMyShared.fulfilled, (state, action) => {
        state.loading = false;
        // Combine shareLinks and userShares into one array
        const shareLinks = Array.isArray(action.payload.shareLinks) ? action.payload.shareLinks : [];
        const userShares = Array.isArray(action.payload.userShares) ? action.payload.userShares : [];
        state.myShared = [...shareLinks, ...userShares];
      })
      .addCase(fetchMyShared.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      // fetchSharedWithMe
      .addCase(fetchSharedWithMe.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSharedWithMe.fulfilled, (state, action) => {
        state.loading = false;
        state.sharedWithMe = action.payload.sharedResources;
      })
      .addCase(fetchSharedWithMe.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      // createFileShare
      .addCase(createFileShare.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createFileShare.fulfilled, (state, action) => {
        state.loading = false;
        state.currentShareLink = action.payload.shareLink?.url || null;
        if (action.payload.shareLink) {
          state.myShared = [...state.myShared, action.payload.shareLink];
        }
      })
      .addCase(createFileShare.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      // createFolderShare
      .addCase(createFolderShare.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createFolderShare.fulfilled, (state, action) => {
        state.loading = false;
        state.currentShareLink = action.payload.shareLink?.url || null;
        if (action.payload.shareLink) {
          state.myShared = [...state.myShared, action.payload.shareLink];
        }
      })
      .addCase(createFolderShare.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      // shareWithUsers
      .addCase(shareWithUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(shareWithUsers.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.permissions) {
          state.myShared = [...state.myShared, ...action.payload.permissions];
        }
      })
      .addCase(shareWithUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      // removeShare
      .addCase(removeShare.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(removeShare.fulfilled, (state, action) => {
        state.loading = false;
        state.myShared = state.myShared.filter(share => share.id !== action.payload.shareId);
      })
      .addCase(removeShare.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      // updateShareExpiration
      .addCase(updateShareExpiration.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateShareExpiration.fulfilled, (state, action) => {
        state.loading = false;
        const shareIndex = state.myShared.findIndex(
          (share) => share.id === action.payload.share.id
        );
        if (shareIndex !== -1) {
          state.myShared[shareIndex] = action.payload.share;
        }
      })
      .addCase(updateShareExpiration.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      // updateSharePassword
      .addCase(updateSharePassword.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateSharePassword.fulfilled, (state, action) => {
        state.loading = false;
        const shareIndex = state.myShared.findIndex(
          (share) => share.id === action.payload.share.id
        );
        if (shareIndex !== -1) {
          state.myShared[shareIndex] = action.payload.share;
        }
      })
      .addCase(updateSharePassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearShareError, setCurrentShareLink } = shareSlice.actions;
export default shareSlice.reducer; 